import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { FuseMockApiService } from '@fuse/lib/mock-api/mock-api.service';
import { labels as labelsData, notes as notesData } from 'app/mock-api/apps/notes/data';
import { FuseMockApiUtils } from '@fuse/lib/mock-api';

@Injectable({
    providedIn: 'root'
})
export class NotesMockApi
{
    private _labels: any[] = labelsData;
    private _notes: any[] = notesData;

    /**
     * Constructor
     */
    constructor(
        private readonly _fuseMockApiService: FuseMockApiService
    ){
        // Register Mock API handlers
        this.registerHandlers();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void
    {
        this._fuseMockApiService.onGet('api/apps/notes/labels').reply(this._getLabelsHandler.bind(this));
        this._fuseMockApiService.onPost('api/apps/notes/labels').reply(this._postLabelsHandler.bind(this));
        this._fuseMockApiService.onPatch('api/apps/notes/labels').reply(this._patchLabelsHandler.bind(this));
        this._fuseMockApiService.onDelete('api/apps/notes/labels').reply(this._deleteLabelsHandler.bind(this));
        this._fuseMockApiService.onPost('api/apps/notes/tasks').reply(this._postTasksHandler.bind(this));
        this._fuseMockApiService.onGet('api/apps/notes/all').reply(this._getAllNotesHandler.bind(this));
        this._fuseMockApiService.onPost('api/apps/notes').reply(this._postNotesHandler.bind(this));
        this._fuseMockApiService.onPatch('api/apps/notes').reply(this._patchNotesHandler.bind(this));
        this._fuseMockApiService.onDelete('api/apps/notes').reply(this._deleteNotesHandler.bind(this));
    }

    /**
     * Handler for GET labels request
     */
    private _getLabelsHandler(): [number, any[]] {
        return [200, cloneDeep(this._labels)];
    }

    /**
     * Handler for POST labels request
     */
    private _postLabelsHandler({ request }): [number, any[]] {
        const label = { id: FuseMockApiUtils.guid(), title: request.body.title };
        this._labels.push(label);
        return [200, cloneDeep(this._labels)];
    }

    /**
     * Handler for PATCH labels request
     */
    private _patchLabelsHandler({ request }): [number, any[]] {
        const updatedLabel = request.body.label;
        this._labels = this._labels.map(label => label.id === updatedLabel.id ? { ...label, title: updatedLabel.title } : label);
        return [200, cloneDeep(this._labels)];
    }

    /**
     * Handler for DELETE labels request
     */
    private _deleteLabelsHandler({ request }): [number, any[]] {
        const id = request.params.get('id');
        this._labels = this._labels.filter(label => label.id !== id);
        this._notes = this._notes.map(note => ({ ...note, labels: note.labels.filter(item => item !== id) }));
        return [200, cloneDeep(this._labels)];
    }

    /**
     * Handler for POST tasks request
     */
    private _postTasksHandler({ request }): [number, any] {
        let updatedNote = request.body.note;
        const task = request.body.task;
        this._notes = this._notes.map(note => {
            if (note.id === updatedNote.id) {
                if (!note.tasks) note.tasks = [];
                note.tasks.push({ id: FuseMockApiUtils.guid(), content: task, completed: false });
                updatedNote = cloneDeep(note);
                return { ...note };
            }
            return note;
        });
        return [200, updatedNote];
    }

    /**
     * Handler for GET all notes request
     */
    private _getAllNotesHandler(): [number, any[]] {
        const labels = cloneDeep(this._labels);
        let notes = cloneDeep(this._notes);
        notes = notes.map(note => ({ ...note, labels: note.labels.map(labelId => labels.find(label => label.id === labelId)) }));
        return [200, notes];
    }

    /**
     * Handler for POST notes request
     */
    private _postNotesHandler({ request }): [number, any] {
        const note = request.body.note;
        note.id = FuseMockApiUtils.guid();
        this._notes.push(note);
        return [200, note];
    }

    /**
     * Handler for PATCH notes request
     */
    private _patchNotesHandler({ request }): [number, any] {
        const updatedNote = request.body.updatedNote;
        this._notes = this._notes.map(note => note.id === updatedNote.id ? { ...updatedNote } : note);
        return [200, updatedNote];
    }

    /**
     * Handler for DELETE notes request
     */
    private _deleteNotesHandler({ request }): [number, boolean] {
        const id = request.params.get('id');
        this._notes = this._notes.filter(note => note.id !== id);
        return [200, true];
    }
}
